import { useContext, useEffect, useEffectEvent, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useNavigate, useParams } from 'react-router'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined'
import { Alert, Box, Button, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import FormikDatepicker from '../../../components/formik-datepicker'
import FormikTextField from '../../../components/formik-textfield'
import { AppContext } from '../../../providers/app-provider'
import Api from '../../../utils/api'
import { RELEASE_NOTES_TAG } from '../../../utils/constants'
import useDocumentTitle from '../../../utils/hooks/useDocumentTitle'
import useSharedStyles from '../../../utils/shared.styles'

import DeleteModal from './delete-modal'
import DiscardModal from './discard-modal'
import useStyles from './index.styles'
import NotesSections from './notes-section-field'
import ReleaseTagField from './release-tag-field'

interface IReleaseNotesFormValues {
  level: string
  title: string
  releaseDate: Date
  notes: Array<{ header: string; content: string }>
}

const ReleaseNote = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles

  const { pageRefreshCounter } = useContext(AppContext)
  const auth = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { releaseNoteId } = useParams()
  const isEditMode = releaseNoteId !== 'new'

  const isDirtyRef = useRef<boolean>(false)

  const [releaseNote, setReleaseNote] = useState<IReleaseNoteType | null>(null)
  const [releaseNoteLoading, setReleaseNoteLoading] = useState<boolean>(false)
  const [releaseNoteError, setReleaseNoteError] = useState<string>('')

  const [showDiscardModal, setShowDiscardModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  useDocumentTitle('Meshify Central')

  const getReleaseNote = useEffectEvent(async () => {
    if (!auth.user?.access_token) return
    setReleaseNoteLoading(true)
    const res = await Api.get(`/api/v2/releases/${releaseNoteId}`)
    setReleaseNoteLoading(false)

    if (!res.ok) {
      setReleaseNoteError('Could not load release notes at this time. Please try again later')
    } else {
      setReleaseNote(res.body)
    }
  })
  useEffect(() => {
    if (isEditMode) {
      getReleaseNote()
    }
  }, [isEditMode, releaseNoteId, pageRefreshCounter])

  const initialValues = releaseNote
    ? {
        level: releaseNote.level,
        title: releaseNote?.title,
        releaseDate: new Date(releaseNote.releaseDate),
        notes: releaseNote.notes,
      }
    : {
        level: '',
        releaseDate: new Date(),
        title: '',
        notes: [{ header: '', content: '' }],
      }

  const schema = Yup.object().shape({
    level: Yup.string().required('required'),
    releaseDate: Yup.date().required('required'),
    title: Yup.string().required('required'),
  })

  const submitReleaseNotes = async (values: IReleaseNotesFormValues, helpers: FormikHelpers<IReleaseNotesFormValues>) => {
    if (!auth.user?.access_token) return
    helpers.setSubmitting(true)

    const payload = {
      level: values.level,
      title: values?.title,
      releaseDate: values.releaseDate,
      notes: values.notes,
      tags: [RELEASE_NOTES_TAG],
    }

    if (isEditMode) {
      const res = await Api.put(`/api/v2/releases/${releaseNote?.id}`, payload)
      helpers.setSubmitting(false)

      if (res.ok) {
        enqueueSnackbar('Release note updated')
        navigate('/release-notes')
      } else {
        enqueueSnackbar('Unable to publish. Please try again', { variant: 'error' })
      }
    } else {
      const res = await Api.post('/api/v2/releases', payload)
      helpers.setSubmitting(false)

      if (res.ok) {
        navigate('/release-notes')
        enqueueSnackbar('Release note published')
      } else {
        enqueueSnackbar('Unable to publish. Please try again', { variant: 'error' })
      }
    }
  }

  const handleDiscard = (isDirty: boolean) => {
    if (!isDirty) {
      navigate('/release-notes')
    } else {
      setShowDiscardModal(true)
    }
  }

  return (
    <>
      <Grid container={true} spacing={3} sx={classes.container}>
        <Grid size={12}>
          <Button onClick={() => handleDiscard(isDirtyRef.current)} startIcon={<KeyboardArrowLeftOutlinedIcon />} sx={classes.backButton}>
            Back to Release Notes
          </Button>
        </Grid>

        <Grid size={12}>
          <Typography variant="h6">{isEditMode ? 'Edit Release Notes' : 'New Release Note'}</Typography>
        </Grid>

        <Grid
          size={{
            xs: 12,
            lg: 9,
          }}
        >
          <Divider />
        </Grid>

        {releaseNoteLoading && !releaseNoteError && (
          <Grid container={true} spacing={12} sx={[sharedClasses.centerContentHorizontal, classes.extraTopMargin]}>
            <CircularProgress />
          </Grid>
        )}

        {!releaseNoteLoading && releaseNoteError && (
          <Grid container={true} spacing={12} sx={[sharedClasses.centerContentHorizontal, classes.extraTopMargin]}>
            <Alert severity="error">{releaseNoteError}</Alert>
          </Grid>
        )}

        {!releaseNoteLoading && !releaseNoteError && (
          <Grid
            container={true}
            spacing={0}
            size={{
              xs: 12,
              lg: 9,
            }}
          >
            <Formik initialValues={initialValues} onSubmit={submitReleaseNotes} validationSchema={schema} enableReinitialize={true}>
              {({ isValid, isSubmitting, values, dirty }: FormikProps<IReleaseNotesFormValues>) => {
                isDirtyRef.current = dirty

                return (
                  <Form style={classes.form}>
                    <Grid size={12}>
                      <FormikDatepicker name="releaseDate" label="Release Date" required={true} />
                    </Grid>
                    <Grid sx={classes.chipsWrapper} size={12}>
                      <ReleaseTagField />
                    </Grid>
                    <Grid size={12}>
                      <FormikTextField name="title" label="Title" required={true} hideErrors={true} />
                    </Grid>
                    <Grid size={12}>
                      <NotesSections notes={values.notes} />
                    </Grid>
                    <Grid sx={classes.formFooter} size={12}>
                      <Divider sx={classes.footerDivider} />

                      <Box
                        sx={[
                          {
                            maxWidth: 'xl',
                          },
                          classes.footerButton,
                        ]}
                      >
                        <Button variant="contained" type="submit" disabled={!isValid || isSubmitting || !dirty} sx={classes.rightMargin}>
                          Publish
                        </Button>

                        <Button variant="outlined" sx={classes.rightMargin} onClick={() => handleDiscard(dirty)}>
                          Discard
                        </Button>

                        {isEditMode && (
                          <Button color="error" startIcon={<DeleteOutlinedIcon />} onClick={() => setShowDeleteModal(true)}>
                            Delete
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Form>
                )
              }}
            </Formik>
          </Grid>
        )}
      </Grid>
      <DiscardModal open={showDiscardModal} setOpen={setShowDiscardModal} />
      {isEditMode && releaseNote && <DeleteModal open={showDeleteModal} setOpen={setShowDeleteModal} releaseNoteId={releaseNote.id} />}
    </>
  )
}

export default ReleaseNote
